/** Globale UI-Zustände: Toasts, Bestätigungs- und Eingabedialog, Aktions-Sheet, Layout. */

export type ToastTone = "info" | "success" | "error";

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  action?: { label: string; run: () => void };
}

interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

interface PromptRequest {
  title: string;
  label: string;
  value: string;
  confirmLabel: string;
  maxLength: number;
  resolve: (value: string | null) => void;
}

class Ui {
  toasts = $state<Toast[]>([]);
  confirmRequest = $state<ConfirmRequest | null>(null);
  promptRequest = $state<PromptRequest | null>(null);
  /** Dokument, für das das Aktions-Sheet offen ist */
  actionsFor = $state<string | null>(null);
  uploadOpen = $state(false);
  wide = $state(true);
  landscape = $state(true);
  private seq = 0;

  constructor() {
    const wide = matchMedia("(min-width: 900px)");
    const landscape = matchMedia("(orientation: landscape)");
    this.wide = wide.matches;
    this.landscape = landscape.matches;
    wide.addEventListener("change", (e) => (this.wide = e.matches));
    landscape.addEventListener("change", (e) => (this.landscape = e.matches));
  }

  toast(message: string, tone: ToastTone = "info", action?: Toast["action"]): void {
    const id = ++this.seq;
    this.toasts = [...this.toasts.slice(-2), { id, message, tone, ...(action ? { action } : {}) }];
    setTimeout(() => this.dismiss(id), action ? 5000 : 2600);
  }

  error(error: unknown, fallback = "Das hat nicht geklappt"): void {
    const message = error instanceof Error && error.message ? error.message : fallback;
    this.toast(message, "error");
  }

  dismiss(id: number): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  confirm(options: {
    title: string;
    message: string;
    confirmLabel?: string;
    danger?: boolean;
  }): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmRequest = {
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel ?? "OK",
        danger: options.danger ?? false,
        resolve: (ok) => {
          this.confirmRequest = null;
          resolve(ok);
        },
      };
    });
  }

  /** Fragt einen kurzen Text ab; `null` bei Abbruch oder leerer Eingabe. */
  prompt(options: {
    title: string;
    label: string;
    value?: string;
    confirmLabel?: string;
    maxLength?: number;
  }): Promise<string | null> {
    return new Promise((resolve) => {
      this.promptRequest = {
        title: options.title,
        label: options.label,
        value: options.value ?? "",
        confirmLabel: options.confirmLabel ?? "Speichern",
        maxLength: options.maxLength ?? 120,
        resolve: (value) => {
          this.promptRequest = null;
          const text = value?.trim() ?? "";
          resolve(text ? text.slice(0, options.maxLength ?? 120) : null);
        },
      };
    });
  }
}

export const ui = new Ui();
